import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { useFormik, FormikProvider } from 'formik';
import * as Yup from 'yup';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import CustomLoader from '../../../common_components/CustomLoader';
import AppButton from '../../../common_components/AppButton';
import '../../../App.css';
import { TextInput, TextArea, Dropdown } from '../../../common_components/FormInputs';
const drawerWidth = 600;
const getValidationSchema = () => {
    return Yup.object({
        name: Yup.string()
            .trim()
            .max(100, 'Name cannot exceed 100 characters'),
        email: Yup.string()
            .trim()
            .required('Email is required')
            .email('Enter a valid email address')
            .max(150, 'Email cannot exceed 150 characters'),
        status: Yup.string()
            .required('Status is required')
            .oneOf(['active', 'inactive'], 'Invalid status'),
        notes: Yup.string()
            .max(1000, 'Notes cannot exceed 1000 characters'),
    });
};
function AddEditDrawer({ open, onClose, contactData = null, onSave, }) {
    const isEditMode = !!contactData;
    const formik = useFormik({
        initialValues: {
            name: contactData?.name || '',
            email: contactData?.email || '',
            status: contactData?.status || 'active',
            notes: contactData?.notes || '',
        },
        enableReinitialize: true,
        validationSchema: getValidationSchema(),
        validateOnBlur: true,
        validateOnChange: true,
        onSubmit: async (values, { resetForm, setSubmitting, setErrors }) => {
            try {
                if (onSave) {
                    const result = await onSave(values);
                    if (result === true) {
                        if (!isEditMode) {
                            resetForm();
                        }
                    }
                    else if (result &&
                        typeof result === 'object' &&
                        'errors' in result) {
                        setErrors(result.errors);
                    }
                }
            }
            catch (e) {
                console.error('save opt out contact error', e);
            }
            finally {
                setSubmitting(false);
            }
        },
    });
    const handleChange = (name, e) => {
        const value = e?.target?.value ?? '';
        formik.setFieldValue(name, value);
    };
    const handleStatusChange = (value) => {
        const v = Array.isArray(value) ? value[0] : value;
        formik.setFieldValue('status', v ?? '');
        formik.setFieldTouched('status', true, false);
    };
    const handleBlur = (name) => {
        formik.setFieldTouched(name, true);
    };
    return (_jsx(Drawer, { anchor: 'right', className: 'user-list-edit', open: open, onClose: onClose, sx: {
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
                width: drawerWidth,
                boxSizing: 'border-box',
            },
        }, children: _jsx(FormikProvider, { value: formik, children: _jsxs(Box, { sx: {
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    backgroundColor: '#f9fafb',
                }, children: [_jsxs(Box, { sx: {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '10px',
                            height: '60px',
                            borderBottom: '1px solid #e9eaeb',
                            backgroundColor: '#fafafa',
                        }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: '10px' }, children: [_jsx(IconButton, { onClick: onClose, size: 'small', children: _jsx(CloseIcon, {}) }), _jsx("div", { className: 'line' }), _jsx(Typography, { sx: {
                                            fontSize: '16px',
                                            fontWeight: 500,
                                            color: '#18181b',
                                            lineHeight: '24px',
                                        }, className: 'drawer-main-title', children: isEditMode ? 'Edit Opt-Out Contact' : 'Add New Opt-Out Contact' })] }), _jsx(AppButton, { variant: 'contained', onClick: () => formik.handleSubmit(), startIcon: _jsx(SaveIcon, {}), disabled: formik.isSubmitting, color: 'primary', className: 'app-common-button', children: formik.isSubmitting ? (_jsx(CustomLoader, { sx: { color: 'white' }, size: 18 })) : ('Save') })] }), _jsx(Box, { className: 'form-input-root gap-responsive', component: 'form', onSubmit: formik.handleSubmit, sx: {
                            flex: 1,
                            overflowY: 'auto',
                            padding: '8px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '20px',
                            position: 'relative',
                            backgroundColor: '#f4f4f5',
                        }, children: _jsxs(Box, { sx: {
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '20px',
                                backgroundColor: '#ffffff',
                                border: '1px solid #e5e7eb',
                                borderRadius: '8px',
                                padding: '20px',
                            }, children: [_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: '10px' }, children: [_jsx(Typography, { sx: {
                                                fontSize: '18px',
                                                fontWeight: 600,
                                                color: '#ff008e',
                                                lineHeight: '28px',
                                            }, className: 'field-drawer-title', children: "Contact Information" }), _jsx(Box, { sx: {
                                                height: '1px',
                                                backgroundColor: '#e9eaeb',
                                                width: '100%',
                                            } })] }), _jsxs(Box, { sx: {
                                        display: 'grid',
                                        gridTemplateColumns: {
                                            xs: '1fr',
                                            sm: '1fr 1fr',
                                            md: '1fr 1fr 1fr',
                                        },
                                        gap: '20px',
                                        alignItems: 'flex-start',
                                    }, children: [_jsx(Box, { children: _jsx(TextInput, { label: 'Name', type: 'text', maxLength: 100, value: formik.values.name, placeholder: 'Enter name', onChange: (e) => handleChange('name', e), onBlur: () => handleBlur('name'), autoComplete: 'off', error: Boolean(formik.touched.name && formik.errors.name), errorMessage: formik.touched.name && formik.errors.name
                                                    ? formik.errors.name
                                                    : undefined }) }), _jsx(Box, { children: _jsx(TextInput, { label: 'Email', type: 'text', required: true, maxLength: 150, value: formik.values.email, placeholder: 'Enter email', onChange: (e) => handleChange('email', e), onBlur: () => handleBlur('email'), autoComplete: 'off', error: Boolean(formik.touched.email && formik.errors.email), errorMessage: formik.touched.email && formik.errors.email
                                                    ? formik.errors.email
                                                    : undefined }) }), _jsx(Box, { children: _jsx(Dropdown, { label: 'Status', value: formik.values.status, onChange: handleStatusChange, placeholder: 'Select status', options: [
                                                    { value: 'active', label: 'Active' },
                                                    { value: 'inactive', label: 'Inactive' },
                                                ], required: true, error: Boolean(formik.touched.status && formik.errors.status), errorMessage: formik.touched.status && formik.errors.status
                                                    ? formik.errors.status
                                                    : undefined }) })] }), _jsx(Box, { sx: { width: '100%' }, children: _jsx(TextArea, { label: 'Notes', minRows: 4, maxLength: 1000, value: formik.values.notes, placeholder: 'Enter notes', onChange: (e) => handleChange('notes', e), onBlur: () => handleBlur('notes'), error: Boolean(formik.touched.notes && formik.errors.notes), errorMessage: formik.touched.notes && formik.errors.notes
                                            ? formik.errors.notes
                                            : undefined }) })] }) })] }) }) }));
}
export default React.memo(AddEditDrawer);
